import type { PagingActions, PagingOptions } from "../../models/types"
import {
  getAllThemes,
  getTheme,
  getThemesByChapterId,
  getThemesByParentAndLicenceIds,
  getThemesByRegionId,
  postTheme,
  putTheme,
  removeTheme
} from "../../services/ThemeService"
import { getQueryString } from "../../utils"
import { history } from "../Store"
import type { AppState } from "../app.state"
import type { Chapter } from "../chapters/chapter.types"
import type { Region } from "../regions/region.types"
import type { PaginatedListOfThemeOverviewDto, ThemeDetailDto, ThemeDto, ThemeOverviewDto } from "./../../models/models"
import type { AppThunkAction, AppThunkDispatch } from "./../app.types"
import type { Theme, ThemeAction, ThemeListAction } from "./theme.types"

export const THEME_CREATE: string = "THEME_CREATE"

export const THEME_FETCH_LIST: string = "THEME_FETCH_LIST"
export const THEME_FETCH: string = "THEME_FETCH"

export const THEME_UPDATE: string = "THEME_UPDATE"

export const THEME_DELETE: string = "THEME_DELETE"

export const THEME_LOAD_DEFAULT: string = "THEME_LOAD_DEFAULT"
export const THEME_LOAD_REGION_LIST: string = "THEME_LOAD_REGION_LIST"
export const THEME_LOAD_CHAPTER_LIST: string = "THEME_LOAD_CHAPTER_LIST"
export const THEME_LOAD_LIST: string = "THEME_LOAD_LIST"
export const THEME_LOAD: string = "THEME_LOAD"

export const THEME_CHANGE_PAGE: string = "THEME_CHANGE_PAGE"
export const THEME_LOAD_LIST_OPTIONS: string = "THEME_LOAD_LIST_OPTIONS"

export function createTheme(theme: Theme): AppThunkAction<void, ThemeAction> {
  return async (dispatch: AppThunkDispatch<ThemeAction>) => {
    dispatch({ type: THEME_CREATE })
    const newThemeId: string = await postTheme(filterThemeWhitespaces(theme))
    const newTheme: ThemeDetailDto = await getTheme(newThemeId)

    history.replace(`/manage/themes/${newThemeId}`)
    dispatch(loadTheme(newTheme))
  }
}

export function fetchAllThemes(pageIndex = 0, pageSize = 50): AppThunkAction<void, ThemeListAction | PagingActions> {
  return async (dispatch: AppThunkDispatch<ThemeListAction | PagingActions>, getState: () => AppState) => {
    dispatch({ type: THEME_FETCH_LIST })

    const state: AppState = getState()
    const queryString: string = getQueryString(pageIndex, pageSize, state.contentManager.sort)

    const pagedList: PaginatedListOfThemeOverviewDto = await getAllThemes(queryString)
    const pagingOptions: PagingOptions = {
      currentPage: pagedList.pageIndex,
      totalPages: pagedList.totalPages,
      pageSize: pageSize
    }

    dispatch(loadThemeRegionList(pagedList.items as ThemeOverviewDto[]))
    dispatch({ type: THEME_LOAD_LIST_OPTIONS, payload: pagingOptions })
  }
}

export function fetchThemeListByParentAndLicenceIds(
  chapterId: string | undefined,
  regionId: string | undefined,
  licenceIds: string[]
): AppThunkAction<void, ThemeListAction> {
  return async (dispatch: AppThunkDispatch<ThemeListAction>) => {
    dispatch({ type: THEME_FETCH_LIST })

    const themes: ThemeDto[] = await getThemesByParentAndLicenceIds(chapterId, regionId, licenceIds)

    if (regionId) dispatch(loadThemeRegionList(themes))
    if (chapterId) dispatch(loadThemeChapterList(themes))
  }
}

export function fetchThemesByChapterOrRegion(
  chapterId: string | undefined,
  regionId: string | undefined
): AppThunkAction<void, ThemeListAction> {
  return async (dispatch: AppThunkDispatch<ThemeListAction>) => {
    if (chapterId && !regionId) {
      dispatch(fetchThemesByChapter(chapterId))
    } else if (!chapterId && regionId) {
      dispatch(fetchThemesByRegion(regionId))
    }
  }
}

export function fetchThemesByChapter(id: string): AppThunkAction<void, ThemeListAction> {
  return async (dispatch: AppThunkDispatch<ThemeListAction>) => {
    dispatch({ type: THEME_FETCH_LIST })

    const themes: ThemeDto[] = await getThemesByChapterId(id)

    dispatch(loadThemeChapterList(themes))
  }
}

export function fetchThemesByRegion(id: string): AppThunkAction<void, ThemeListAction> {
  return async (dispatch: AppThunkDispatch<ThemeListAction>) => {
    dispatch({ type: THEME_FETCH_LIST })

    const themes: ThemeDto[] = await getThemesByRegionId(id)

    dispatch(loadThemeRegionList(themes))
  }
}

export function fetchTheme(id: string): AppThunkAction<void, ThemeAction> {
  return async (dispatch: AppThunkDispatch<ThemeAction>) => {
    dispatch({ type: THEME_FETCH })

    const theme: ThemeDetailDto = await getTheme(id)

    dispatch(loadTheme(theme))
  }
}

export function updateTheme(theme: Theme): AppThunkAction<void, ThemeAction> {
  return async (dispatch: AppThunkDispatch<ThemeAction>) => {
    dispatch({ type: THEME_UPDATE })
    await putTheme(filterThemeWhitespaces(theme))
    const updatedTheme: ThemeDetailDto = await getTheme(theme.id as string)
    dispatch(loadTheme(updatedTheme))
  }
}

export function deleteTheme(id: string): AppThunkAction<void, ThemeAction> {
  return async (dispatch: AppThunkDispatch<ThemeAction>) => {
    dispatch({ type: THEME_DELETE })
    await removeTheme(id)
    dispatch(fetchAllThemes())
  }
}

export function changeThemePage(page: number): AppThunkAction<void, PagingActions> {
  return (dispatch: AppThunkDispatch<PagingActions>, getState: () => AppState) => {
    const state = getState()
    dispatch({ type: THEME_CHANGE_PAGE, payload: page })

    dispatch(fetchAllThemes(page, state.theme.pagingOptions.pageSize))
  }
}

export function loadDefaultTheme(): ThemeAction {
  return {
    type: THEME_LOAD_DEFAULT
  }
}

export function loadThemeRegionList(dtoList: ThemeOverviewDto[]): ThemeListAction {
  const themeList: Theme[] = dtoList.map(themeOverviewDtoToTheme)
  return {
    type: THEME_LOAD_REGION_LIST,
    payload: themeList
  }
}

export function loadThemeChapterList(dtoList: ThemeOverviewDto[]): ThemeListAction {
  return {
    type: THEME_LOAD_CHAPTER_LIST,
    payload: dtoList.map(themeOverviewDtoToTheme)
  }
}

export function loadThemeList(dtoList: ThemeOverviewDto[]): ThemeListAction {
  return {
    type: THEME_LOAD_LIST,
    payload: dtoList.map(themeOverviewDtoToTheme)
  }
}

export function loadTheme(dto: ThemeDetailDto): ThemeAction {
  const theme: Theme = themeDetailDtoToTheme(dto)
  return {
    type: THEME_LOAD,
    payload: theme
  }
}

function themeDetailDtoToTheme(dto: ThemeDetailDto): Theme {
  const licenceIds: string[] | undefined = dto.licences?.map(({ id }) => id)
  return {
    ...dto,
    id: dto.id,
    name: dto.name as string,
    chapterId: dto.chapter?.id,
    chapter: dto.chapter as Chapter,
    regionId: dto.region?.id,
    region: dto.region as Region,
    parent: "",
    licenceIds: licenceIds ? licenceIds : []
  }
}

function themeOverviewDtoToTheme(dto: ThemeOverviewDto): Theme {
  return {
    ...dto,
    id: dto.id,
    name: dto.name as string,
    parent: dto.parent as string,
    licenceIds: []
  }
}

function filterThemeWhitespaces(theme: Theme): Theme {
  return {
    ...theme,
    chapterId: theme.chapterId === " " ? undefined : theme.chapterId,
    regionId: theme.regionId === " " ? undefined : theme.regionId
  }
}
